import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Globe, Plus, ExternalLink, Eye, Settings, Trash2, Check, X, Copy, Link2, BookOpen, RefreshCw, Edit2, Share2, Lock } from 'lucide-react';

export default function PublishDocsPage({ projectId, project }) {
  const [sites, setSites] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showShareCreate, setShowShareCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createForm, setCreateForm] = useState({ name: project?.name || 'My API Docs', description: '', visibility: 'public', theme: 'default' });
  const [shareForm, setShareForm] = useState({ expires_hours: 24, password: '' });

  useEffect(() => { if (projectId) { loadSites(); loadShareLinks(); } }, [projectId]);
  useEffect(() => { if (project) setCreateForm(f => ({ ...f, name: project.name || '' })); }, [project]);

  async function loadSites() {
    setLoading(true);
    try { const r = await api.get(`/projects/${projectId}/publish-sites`); setSites(r.data); if (r.data.length && !selected) setSelected(r.data[0]); }
    catch {} finally { setLoading(false); }
  }
  async function loadShareLinks() {
    try { const r = await api.get(`/projects/${projectId}/share-links`); setShareLinks(r.data); } catch {}
  }

  async function createSite(e) {
    e.preventDefault();
    try {
      const r = await api.post(`/projects/${projectId}/publish-sites`, createForm);
      toast.success('Docs site created!');
      setShowCreate(false);
      setCreateForm({ name: project?.name || '', description: '', visibility: 'public', theme: 'default' });
      loadSites();
      setSelected(r.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function togglePublish(site) {
    try {
      const action = site.status === 'published' ? 'unpublish' : 'publish';
      const r = await api.post(`/publish-sites/${site.id}/${action}`);
      toast.success(action === 'publish' ? 'Published! 🎉' : 'Unpublished');
      if (r.data?.id) setSelected(r.data); // refresh status so the UI/URL reflect reality
      loadSites();
    } catch { toast.error('Failed'); }
  }

  async function saveEdit(e) {
    e.preventDefault();
    try {
      const r = await api.put(`/publish-sites/${editing.id}`, editing);
      toast.success('Settings saved!');
      if (r.data?.id) setSelected(r.data);
      setEditing(null);
      loadSites();
    } catch { toast.error('Failed'); }
  }

  async function deleteSite(id) {
    if (!window.confirm('Delete this docs site?')) return;
    try { await api.delete(`/publish-sites/${id}`); toast.success('Deleted'); loadSites(); setSelected(null); }
    catch { toast.error('Failed'); }
  }

  async function createShareLink(e) {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/share-links`, shareForm);
      toast.success('Share link created!');
      setShowShareCreate(false);
      setShareForm({ expires_hours: 24, password: '' });
      loadShareLinks();
    } catch { toast.error('Failed'); }
  }

  // Real, reachable public URL served by this app
  const publicUrl = (site) => `${window.location.origin}/docs/${site.slug}`;

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* Left panel */}
      <div style={{ width:280, borderRight:'1px solid var(--border)', background:'var(--bg-secondary)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'14px 14px 10px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:14, color:'var(--text-1)' }}>Share Docs</div>

          {/* Quick Share section */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>Quick Share</div>
            <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
              <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                  <Share2 size={13} style={{ color:'var(--text-3)' }} />
                  <span style={{ fontSize:12, fontWeight:500 }}>Shared Docs List</span>
                </div>
                <div style={{ fontSize:11, color:'var(--text-3)' }}>Share docs temporarily</div>
                <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>{shareLinks.length} shares</div>
              </div>
              <div style={{ padding:'8px 12px' }}>
                <button className="btn btn-sm btn-secondary" style={{ width:'100%', justifyContent:'center' }} onClick={() => setShowShareCreate(true)}>
                  <Plus size={12} /> New Share Link
                </button>
              </div>
            </div>
          </div>

          {/* Publish sites */}
          <div style={{ fontSize:11, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>Publish Docs Sites</div>
        </div>

        {/* Sites list */}
        <div style={{ flex:1, overflow:'auto', padding:'6px 10px' }}>
          {loading && <div style={{ textAlign:'center', padding:20, color:'var(--text-3)', fontSize:12 }}><RefreshCw size={14} className="spin" /></div>}
          {sites.map(site => (
            <div key={site.id} className={`docs-site-card ${selected?.id === site.id ? 'selected' : ''}`}
              style={{ marginBottom:8 }} onClick={() => setSelected(site)}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#5865f2,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <BookOpen size={14} color="#fff" />
                </div>
                <div style={{ flex:1, overflow:'hidden' }}>
                  <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{site.name}</div>
                  <div style={{ fontSize:11, color:'var(--text-3)' }}>/docs/{site.slug}</div>
                </div>
                <button className="btn-icon" style={{ padding:3, flexShrink:0 }} onClick={e => { e.stopPropagation(); deleteSite(site.id); }}><Trash2 size={12} style={{ color:'var(--red)' }}/></button>
              </div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span className={`badge badge-${site.status}`}>{site.status === 'published' ? 'Published' : 'Draft'}</span>
                <div style={{ display:'flex', gap:4 }}>
                  <button className="btn-icon" style={{ padding:3 }} onClick={e => { e.stopPropagation(); setEditing({...site}); }} title="Settings"><Settings size={12}/></button>
                  {site.status === 'published' && <button className="btn-icon" style={{ padding:3 }} onClick={e => { e.stopPropagation(); window.open(publicUrl(site),'_blank'); }} title="Open"><ExternalLink size={12}/></button>}
                </div>
              </div>
            </div>
          ))}
          <button style={{ width:'100%', border:'1px dashed var(--border)', borderRadius:8, padding:'10px', background:'none', cursor:'pointer', color:'var(--accent)', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}
            onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New Docs Site
          </button>
        </div>
      </div>

      {/* Right panel - site detail */}
      <div style={{ flex:1, overflow:'auto', background:'var(--bg)' }}>
        {!selected && !loading && (
          <div className="empty-state">
            <BookOpen size={48} />
            <h3>No docs site selected</h3>
            <p>Create a docs site to publish your API documentation publicly.</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={14}/> Create Docs Site</button>
          </div>
        )}

        {selected && (
          <div style={{ maxWidth:720, margin:'0 auto', padding:'28px 32px' }}>
            {/* Site header */}
            <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:28, padding:20, background:'var(--bg-secondary)', borderRadius:12, border:'1px solid var(--border)' }}>
              <div style={{ width:56, height:56, borderRadius:12, background:'linear-gradient(135deg,#5865f2,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <BookOpen size={24} color="#fff" />
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>{selected.name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <code style={{ fontSize:12, color:'var(--text-2)' }}>{publicUrl(selected)}</code>
                  <button className="btn-icon" style={{ padding:2 }} onClick={() => { navigator.clipboard.writeText(publicUrl(selected)); toast.success('Copied!'); }}><Copy size={11}/></button>
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <button className="btn btn-secondary btn-sm" style={{ marginBottom:6, display:'flex', alignItems:'center', gap:5 }}
                  onClick={() => window.open(selected.status === 'published' ? publicUrl(selected) : `/docs/${selected.slug}`, '_blank')}>
                  <Eye size={13} /> Preview
                </button>
                <div style={{ fontSize:11, color:'var(--text-3)' }}>
                  {selected.status === 'published' ? `${Math.floor((Date.now() - new Date(selected.published_at)) / 86400000)} days ago` : 'Not published'}
                </div>
              </div>
            </div>

            {/* Publish status */}
            <Section title="Publish Status">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span className={`badge badge-${selected.status === 'published' ? 'published' : 'draft'}`}>
                    {selected.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                  {selected.status === 'published' && <a href={publicUrl(selected)} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'var(--accent)', display:'flex', alignItems:'center', gap:4 }}><ExternalLink size={12}/> View live</a>}
                </div>
                <button className={`btn btn-sm ${selected.status==='published' ? 'btn-secondary' : 'btn-primary'}`} onClick={() => togglePublish(selected)}>
                  {selected.status === 'published' ? 'Unpublish' : 'Publish'}
                </button>
              </div>
            </Section>

            {/* Visibility */}
            <Section title="Visibility">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  {selected.visibility === 'public' ? <Globe size={15} style={{ color:'var(--green)' }}/> : <Lock size={15} style={{ color:'var(--orange)' }}/>}
                  <span style={{ fontSize:13, fontWeight:500, textTransform:'capitalize' }}>{selected.visibility}</span>
                </div>
                <button className="btn btn-sm btn-secondary" onClick={() => setEditing({...selected})}>Edit</button>
              </div>
            </Section>

            {/* Release */}
            <Section title="Release">
              <div style={{ display:'grid', gap:12 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:3 }}>Source Branch</div>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <svg width="12" height="14" viewBox="0 0 16 20" fill="none"><path d="M8 0L16 4V10L8 20L0 10V4L8 0Z" fill="#6b7280"/></svg>
                      <span style={{ fontSize:13 }}>{selected.branch || 'main'}</span>
                    </div>
                  </div>
                  <button className="btn btn-sm btn-secondary" onClick={() => setEditing({...selected})}>Edit</button>
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:3 }}>Environment</div>
                    <span style={{ fontSize:13, color:'var(--orange)', background:'#fff7ed', padding:'2px 8px', borderRadius:4 }}>No Environment</span>
                    <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3 }}>The environment's Base URL will be displayed in the docs site.</div>
                  </div>
                  <button className="btn btn-sm btn-secondary" onClick={() => setEditing({...selected})}>Edit</button>
                </div>
              </div>
            </Section>

            {/* Basic Settings */}
            <Section title="Basic Settings">
              <div style={{ display:'grid', gap:10 }}>
                {[
                  { label:'Site Title', key:'site_title', value: selected.site_title || selected.name, desc:'This title affects how your site is displayed when shared or indexed by search engines.' },
                ].map(item => (
                  <div key={item.key} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, marginBottom:3 }}>{item.label}</div>
                      <div style={{ fontSize:13, color:'var(--text-2)' }}>{item.value || '—'}</div>
                      {item.desc && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3 }}>{item.desc}</div>}
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditing({...selected})}>Edit</button>
                  </div>
                ))}
              </div>
            </Section>

            {/* Share links section */}
            <Section title="Quick Share Links" desc="Share docs temporarily without publishing">
              {shareLinks.length === 0 && <div style={{ fontSize:12, color:'var(--text-3)', padding:'8px 0' }}>No share links created yet.</div>}
              {shareLinks.map(sl => (
                <div key={sl.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                  <Link2 size={14} style={{ color:'var(--text-3)', flexShrink:0 }} />
                  <code style={{ flex:1, fontSize:11, color:'var(--accent)' }}>
                    {window.location.origin}{sl.url}
                  </code>
                  <span style={{ fontSize:11, color:'var(--text-3)' }}>{sl.view_count} views</span>
                  <span style={{ fontSize:11, color:'var(--text-3)' }}>{sl.expires_at ? `Expires ${new Date(sl.expires_at).toLocaleDateString()}` : 'No expiry'}</span>
                  <button className="btn-icon" style={{ padding:3 }} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${sl.url}`); toast.success('Copied!'); }}><Copy size={11}/></button>
                </div>
              ))}
              <button className="btn btn-sm btn-secondary" style={{ marginTop:8 }} onClick={() => setShowShareCreate(true)}>
                <Plus size={12}/> New Share Link
              </button>
            </Section>
          </div>
        )}
      </div>

      {/* Create Site Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">New Docs Site</div>
              <button className="btn-icon" onClick={() => setShowCreate(false)}><X size={16}/></button>
            </div>
            <form onSubmit={createSite}>
              <div className="form-group"><label className="form-label">Site Name *</label>
                <input value={createForm.name} onChange={e => setCreateForm(f=>({...f,name:e.target.value}))} placeholder="My API Documentation" required autoFocus /></div>
              <div className="form-group"><label className="form-label">Description</label>
                <textarea rows={2} value={createForm.description} onChange={e => setCreateForm(f=>({...f,description:e.target.value}))} placeholder="Brief description of your API docs" /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Visibility</label>
                  <select value={createForm.visibility} onChange={e => setCreateForm(f=>({...f,visibility:e.target.value}))}>
                    <option value="public">Public — Anyone can view</option>
                    <option value="private">Private — Only team members</option>
                  </select></div>
                <div className="form-group"><label className="form-label">Theme</label>
                  <select value={createForm.theme} onChange={e => setCreateForm(f=>({...f,theme:e.target.value}))}>
                    <option value="default">Default</option>
                    <option value="minimal">Minimal</option>
                    <option value="dark">Dark</option>
                  </select></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Site</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Site Modal */}
      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Edit Docs Site Settings</div>
              <button className="btn-icon" onClick={() => setEditing(null)}><X size={16}/></button>
            </div>
            <form onSubmit={saveEdit}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Site Name</label>
                  <input value={editing.name} onChange={e => setEditing(v=>({...v,name:e.target.value}))} required /></div>
                <div className="form-group"><label className="form-label">Site Title (SEO)</label>
                  <input value={editing.site_title||''} onChange={e => setEditing(v=>({...v,site_title:e.target.value}))} placeholder={editing.name} /></div>
              </div>
              <div className="form-group"><label className="form-label">Description</label>
                <textarea rows={2} value={editing.description||''} onChange={e => setEditing(v=>({...v,description:e.target.value}))} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Visibility</label>
                  <select value={editing.visibility} onChange={e => setEditing(v=>({...v,visibility:e.target.value}))}>
                    <option value="public">Public</option><option value="private">Private</option>
                  </select></div>
                <div className="form-group"><label className="form-label">Source Branch</label>
                  <select value={editing.branch||'main'} onChange={e => setEditing(v=>({...v,branch:e.target.value}))}>
                    <option value="main">main</option><option value="develop">develop</option><option value="staging">staging</option>
                  </select></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Theme</label>
                  <select value={editing.theme||'default'} onChange={e => setEditing(v=>({...v,theme:e.target.value}))}>
                    <option value="default">Default</option><option value="minimal">Minimal</option><option value="dark">Dark</option>
                  </select></div>
                <div className="form-group"><label className="form-label">Custom Domain (optional)</label>
                  <input value={editing.custom_domain||''} onChange={e => setEditing(v=>({...v,custom_domain:e.target.value}))} placeholder="docs.yourdomain.com" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Settings</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareCreate && (
        <div className="modal-overlay" onClick={() => setShowShareCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Create Share Link</div>
              <button className="btn-icon" onClick={() => setShowShareCreate(false)}><X size={16}/></button>
            </div>
            <form onSubmit={createShareLink}>
              <div className="form-group"><label className="form-label">Expires in (hours)</label>
                <select value={shareForm.expires_hours} onChange={e => setShareForm(f=>({...f,expires_hours:parseInt(e.target.value)}))}>
                  <option value={1}>1 hour</option><option value={24}>24 hours</option>
                  <option value={168}>7 days</option><option value={720}>30 days</option><option value={0}>Never</option>
                </select></div>
              <div className="form-group"><label className="form-label">Password (optional)</label>
                <input type="password" value={shareForm.password} onChange={e => setShareForm(f=>({...f,password:e.target.value}))} placeholder="Leave blank for no password" /></div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowShareCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Link2 size={13}/> Create Link</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ fontSize:15, fontWeight:600, color:'var(--text-1)', marginBottom:4 }}>{title}</div>
      {desc && <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:10 }}>{desc}</div>}
      <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'14px 18px' }}>
        {children}
      </div>
    </div>
  );
}
